import { useState } from 'react'
import {
  Box,
  Button,
  EmptyState,
  HStack,
  IconButton,
  Input,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  useErrorModal,
  VStack
} from '~/design_system'
import { Msg, useMsg } from '~/common/localizationUtils'
import { ArrowLeft, MoreVertical, Pencil, Plus, Search, X } from '~/assistant/icons'
import { AssistantThread, useAssistantThreadsQuery, useDeleteAssistantThreadMutation } from '~/api/assistant'
import { useChatContext } from '../ChatContext'
import { SparkleSpinner } from './SparkleSpinner'

type ThreadHistoryProps = {
  variant?: 'page' | 'sidebar'
  onBack?: () => void
  onClose?: () => void
  onOpenConversation?: () => void
}

export const ThreadHistory = ({ variant = 'sidebar', onBack, onClose, onOpenConversation }: ThreadHistoryProps) => {
  const msg = useMsg()
  const { showModal: showErrorModal } = useErrorModal()
  const threadsQuery = useAssistantThreadsQuery()
  const deleteThread = useDeleteAssistantThreadMutation()
  const thread = useChatContext()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const isPage = variant === 'page'
  const trimmedSearchTerm = searchTerm.trim()
  const recentConversations = (threadsQuery.data ?? []).filter(conversation =>
    conversation.title.toLowerCase().includes(trimmedSearchTerm.toLowerCase())
  )

  const closeSearch = () => {
    setIsSearchOpen(false)
    setSearchTerm('')
  }

  const handleNewChat = () => {
    thread.startNewThread()
    onOpenConversation?.()
  }

  const handleSelect = (threadId: string) => {
    thread.selectThread(threadId)
    onOpenConversation?.()
  }

  const handleDelete = (id: number, threadId: string) => {
    deleteThread.mutate(id, {
      onSuccess: () => {
        if (thread.activeThreadId === threadId) thread.startNewThread()
      },
      onError: () => {
        showErrorModal({ message: msg('common.ui.error_processing_request_try_again') })
      }
    })
  }

  return (
    <Box display="flex" flexDirection="column" height="100%" w="100%">
      {isPage && (
        <Box
          px={4}
          py={3}
          borderBottom="1px solid"
          borderColor="gray.200"
          display="flex"
          alignItems="center"
          justifyContent="space-between">
          <IconButton
            aria-label={msg('common.ui.back')}
            icon={<ArrowLeft />}
            onClick={onBack}
            variant="ghost"
            size="sm"
            isRound
            showTooltip
          />
          <IconButton
            aria-label={msg('common.ui.close')}
            icon={<X />}
            onClick={onClose}
            variant="ghost"
            size="sm"
            isRound
            showTooltip
          />
        </Box>
      )}

      <Box px={4} pt={5} pb={4}>
        <HStack spacing={3} align="center">
          <Button flex={1} size="sm" leftIcon={isPage ? <Pencil /> : <Plus />} onClick={handleNewChat}>
            <Msg id="assistant.ai.history.new_chat" />
          </Button>
          <IconButton
            aria-label={isSearchOpen ? msg('assistant.ai.history.close_search') : msg('assistant.ai.history.search')}
            icon={isSearchOpen ? <X /> : <Search />}
            onClick={isSearchOpen ? closeSearch : () => setIsSearchOpen(true)}
            variant="ghost"
            size="sm"
            isRound
            showTooltip
          />
        </HStack>
      </Box>

      {isSearchOpen && (
        <Box px={4} pb={2}>
          <Input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') closeSearch()
            }}
            placeholder={msg('assistant.ai.history.search')}
            size="sm"
            autoFocus
          />
        </Box>
      )}

      <Box flex="1" overflowY="auto" px={4} pb={4}>
        <Text as="h3" fontSize="sm" fontWeight="bold" color="gray.900" mb={3}>
          <Msg id="assistant.ai.history.recent" />
        </Text>
        <ConversationList
          isLoading={threadsQuery.isLoading}
          isError={threadsQuery.isError}
          onRetry={() => threadsQuery.refetch()}
          conversations={recentConversations}
          searchTerm={trimmedSearchTerm}
          activeThreadId={thread.activeThreadId}
          onSelect={handleSelect}
          onDelete={handleDelete}
        />
      </Box>

      <Box px={4} py={3} borderTop="1px solid" borderColor="gray.200">
        <Text fontSize="xs" textAlign="center" color="gray.500">
          <Msg id="assistant.ai.history.retention_note" />
        </Text>
      </Box>
    </Box>
  )
}

const ConversationList = ({
  isLoading,
  isError,
  onRetry,
  conversations,
  searchTerm,
  activeThreadId,
  onSelect,
  onDelete
}: {
  isLoading: boolean
  isError: boolean
  onRetry: () => void
  conversations: AssistantThread[]
  searchTerm: string
  activeThreadId: string | null
  onSelect: (threadId: string) => void
  onDelete: (id: number, threadId: string) => void
}) => {
  const msg = useMsg()
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <SparkleSpinner />
      </Box>
    )
  }
  if (isError) {
    return (
      <EmptyState isCompact>
        <EmptyState.RefreshError onRefresh={onRetry} />
      </EmptyState>
    )
  }
  if (conversations.length === 0) {
    return (
      <Text fontSize="sm" color="gray.500">
        {searchTerm ? (
          <Msg id="assistant.ai.history.no_matches" searchTerm={searchTerm} />
        ) : (
          <Msg id="assistant.ai.history.empty" />
        )}
      </Text>
    )
  }
  return (
    <VStack spacing={2} align="stretch">
      {conversations.map(conversation => (
        <HStack
          key={conversation.id}
          spacing={2}
          bg={conversation.thread_id === activeThreadId ? 'blue.50' : 'gray.100'}
          borderRadius="full"
          pl={4}
          pr={2}
          py={2}>
          <Box as="button" flex={1} textAlign="left" overflow="hidden" onClick={() => onSelect(conversation.thread_id)}>
            <Text fontSize="sm" color="gray.800" noOfLines={1}>
              {conversation.title || msg('assistant.ai.thread_selector.new_conversation')}
            </Text>
          </Box>
          <Menu placement="bottom-end" isLazy>
            <MenuButton
              as={IconButton}
              aria-label={msg('assistant.ai.history.more_options')}
              icon={<MoreVertical />}
              variant="ghost"
              size="sm"
              isRound
            />
            <MenuList>
              <MenuItem color="red.600" onClick={() => onDelete(conversation.id, conversation.thread_id)}>
                <Msg id="assistant.ai.history.delete" />
              </MenuItem>
            </MenuList>
          </Menu>
        </HStack>
      ))}
    </VStack>
  )
}
